import React, { useMemo, useState } from 'react'
import { formatLastUsed, maskKeyPreview, validateApiKey } from '../activity-stream/apiKeyUtils.js'

const defaultFormState = {
  label: '',
  apiKey: '',
  writeKey: '',
}

const normalizeFormValue = (value) => (value ?? '').trim()

const StoredApiKeyManager = ({
  isOpen,
  keys,
  activeKeyId,
  onSelect,
  onAdd,
  onUpdate,
  onDelete,
  onClose,
}) => {
  const [isFormVisible, setIsFormVisible] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [formState, setFormState] = useState(defaultFormState)
  const [formError, setFormError] = useState(null)
  const [pendingDeleteId, setPendingDeleteId] = useState(null)

  const hasReachedLimit = keys.length >= 10

  const handleResetForm = () => {
    setEditingId(null)
    setFormState(defaultFormState)
    setFormError(null)
    setIsFormVisible(false)
  }

  const openCreateForm = () => {
    setEditingId(null)
    setFormState(defaultFormState)
    setFormError(null)
    setIsFormVisible(true)
  }

  const openEditForm = (entry) => {
    setEditingId(entry.id)
    setFormState({
      label: entry.label ?? '',
      apiKey: entry.apiKey ?? '',
      writeKey: entry.writeKey ?? '',
    })
    setFormError(null)
    setIsFormVisible(true)
  }

  const handleSave = () => {
    const payload = {
      label: normalizeFormValue(formState.label) || 'Untitled',
      apiKey: normalizeFormValue(formState.apiKey),
      writeKey: normalizeFormValue(formState.writeKey),
    }

    const validation = validateApiKey(payload.apiKey)
    if (!validation.valid) {
      setFormError(validation.message)
      return
    }

    const result = editingId
      ? onUpdate(editingId, payload)
      : onAdd(payload)

    if (!result?.success) {
      setFormError(result?.error ?? 'Unable to persist key.')
      return
    }

    handleResetForm()
  }

  const handleDelete = (id) => {
    if (pendingDeleteId === id) {
      onDelete(id)
      setPendingDeleteId(null)
    } else {
      setPendingDeleteId(id)
    }
  }

  if (!isOpen) {
    return null
  }

  return (
    <div className="as-modal-overlay" onClick={onClose}>
      <div
        className="as-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="stored-api-key-manager-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="as-modal-header">
          <h3 id="stored-api-key-manager-title">API keys</h3>
          <button
            type="button"
            className="as-modal-close"
            aria-label="Close"
            onClick={onClose}
          >
            ×
          </button>
        </header>

        {isFormVisible ? (
          <>
            <div className="as-modal-body">
              <div className="as-field">
                <label htmlFor="stored-key-label">Label</label>
                <input
                  id="stored-key-label"
                  type="text"
                  value={formState.label}
                  placeholder="e.g. Production - US"
                  onChange={(event) =>
                    setFormState((previous) => ({
                      ...previous,
                      label: event.target.value,
                    }))
                  }
                />
              </div>
              <div className="as-field">
                <label htmlFor="stored-api-key">API key</label>
                <input
                  id="stored-api-key"
                  type="text"
                  value={formState.apiKey}
                  placeholder="key-..."
                  onChange={(event) =>
                    setFormState((previous) => ({
                      ...previous,
                      apiKey: event.target.value,
                    }))
                  }
                />
              </div>
              <div className="as-field">
                <label htmlFor="stored-write-key">Write key (optional)</label>
                <input
                  id="stored-write-key"
                  type="text"
                  value={formState.writeKey}
                  placeholder="Write key used for mutations"
                  onChange={(event) =>
                    setFormState((previous) => ({
                      ...previous,
                      writeKey: event.target.value,
                    }))
                  }
                />
              </div>
              {formError ? (
                <div className="as-error-message" role="alert">
                  {formError}
                </div>
              ) : null}
            </div>
            <div className="as-modal-footer">
              <button type="button" onClick={handleResetForm}>
                Cancel
              </button>
              <button type="button" className="as-primary" onClick={handleSave}>
                {editingId ? 'Save changes' : 'Add key'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="as-modal-body">
              {keys.length ? (
                <ul className="as-key-list" role="list">
                  {keys.map((entry) => {
                    const isActive = entry.id === activeKeyId
                    const lastUsed = formatLastUsed(entry.lastUsed)
                    return (
                      <li
                        key={entry.id}
                        className={`as-key-item ${isActive ? 'active' : ''}`}
                        onClick={() => onSelect(entry.id)}
                      >
                        <div className="as-key-main">
                          <input
                            type="radio"
                            name="active-stored-key"
                            className="as-key-radio"
                            checked={isActive}
                            onChange={() => onSelect(entry.id)}
                            aria-label={`Activate ${entry.label}`}
                          />
                          <div className="as-key-info">
                            <div className="as-key-header">
                              <span className="as-key-label">{entry.label}</span>
                              {isActive ? (
                                <span className="as-active-badge">Active</span>
                              ) : null}
                            </div>
                            <div className="as-key-value">
                              {maskKeyPreview(entry.apiKey)}
                            </div>
                            <div className="as-key-meta">
                              Last used: {lastUsed}
                              {entry.partition ? ` • ${entry.partition}` : ''}
                              {entry.region ? ` • ${entry.region}` : ''}
                            </div>
                            {entry.writeKey ? (
                              <div className="as-key-meta">
                                Write key: {maskKeyPreview(entry.writeKey)}
                              </div>
                            ) : null}
                          </div>
                        </div>
                        <div className="as-key-actions">
                          <button
                            type="button"
                            className="as-key-action-btn"
                            onClick={(event) => {
                              event.stopPropagation()
                              openEditForm(entry)
                            }}
                            title="Edit"
                          >
                            ✎
                          </button>
                          <button
                            type="button"
                            className="as-key-action-btn delete"
                            onClick={(event) => {
                              event.stopPropagation()
                              handleDelete(entry.id)
                            }}
                            title="Delete"
                          >
                            {pendingDeleteId === entry.id ? 'Confirm?' : 'Delete'}
                          </button>
                        </div>
                      </li>
                    )
                  })}
                </ul>
              ) : (
                <div className="as-empty-state">
                  <p>No API keys stored yet.</p>
                  <p className="as-empty-hint">
                    Add your first API key to unlock Activity Stream and related surfaces.
                  </p>
                </div>
              )}
            </div>
            <div className="as-modal-footer">
              <button type="button" onClick={onClose}>
                Close
              </button>
              <button
                type="button"
                className="as-primary"
                onClick={openCreateForm}
                disabled={hasReachedLimit}
              >
                + Add key
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default StoredApiKeyManager
